import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const DISMISS_KEY = "pwa-install-snoozed-until";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // re-surface the nudge after 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as "MacIntel" with touch support
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// Subtle sidebar "Install app" nudge.
// - Chrome/Android/desktop: triggers the native install prompt (beforeinstallprompt).
// - iOS Safari (no prompt API): opens a tasteful "Share → Add to Home Screen" guide.
// Hidden when already installed or after the user dismisses it.
export default function PWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now()
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  const ios = isIOS();
  // Only surface when the browser offers install OR it's iOS (manual add).
  if (!deferred && !ios) return null;

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setShowIOSHelp(true);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_MS));
    setDismissed(true);
  };

  return (
    <>
      <div className="mt-2 flex items-center gap-1" data-testid="pwa-install-bar">
        <button
          onClick={handleInstall}
          className="flex-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink bg-gold/90 hover:bg-gold transition-colors font-medium"
          data-testid="pwa-install-button"
        >
          <Download className="h-4 w-4" />
          Install app
        </button>
        <button
          onClick={handleDismiss}
          className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={showIOSHelp} onOpenChange={setShowIOSHelp}>
        <DialogContent className="max-w-sm" data-testid="pwa-ios-help">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Install 3 Realty</DialogTitle>
            <DialogDescription>
              Add the app to your Home Screen for a full-screen, app-like experience.
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold">1</span>
              <span className="flex items-center gap-1.5">
                Tap the <Share className="h-4 w-4 text-blue-600 inline" /> <b>Share</b> button in your browser toolbar.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold">2</span>
              <span className="flex items-center gap-1.5">
                Scroll and choose <Plus className="h-4 w-4 inline" /> <b>Add to Home Screen</b>.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold">3</span>
              <span>Tap <b>Add</b> — the “3 Realty” icon appears on your Home Screen.</span>
            </li>
          </ol>
          <p className="text-xs text-slate-500 mt-1">
            Tip: use Safari on iPhone/iPad for the Add to Home Screen option.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
